/**
 * GlassesUSA-style filters for eyeglasses / sunglasses PLPs.
 * Syncs visible options to hidden native Shopify facet inputs; applies tag/badge/measurement filters client-side.
 *
 * Shape note: Shopify storefront URLs keep only the last filter.v.m.custom.shape value.
 * Multi-mapped shapes (Aviator|Pilot|Navigator, etc.) therefore load via a client union fetch.
 */
(function () {
  const ROOT_SELECTOR = '[data-ciw-gusa-filters]';
  const OPTION_INPUT = '[data-ciw-gusa-option-input]';
  const NATIVE_INPUT = '[data-ciw-gusa-native-input]';
  const TAG_ONLY = '[data-ciw-gusa-tag-only="true"]';
  const CARD_SELECTOR = '#product-grid .ciw-card';
  const CARD_HIDDEN = 'ciw-card--filter-hidden';
  const MEASUREMENT_MIN = '[data-ciw-gusa-measurement-min]';
  const MEASUREMENT_MAX = '[data-ciw-gusa-measurement-max]';
  const CLIENT_FILTER = '[data-ciw-gusa-client-filter]';
  const SHAPE_PARAM = 'filter.v.m.custom.shape';
  const SHAPE_GROUP_PARAM = 'ciw_shape_group';

  let tagOnlyActive = new Map();
  let clientFiltersActive = new Set();
  let measurementFilters = { lensWidth: null, frameWidth: null };
  let debounceTimer = null;
  let shapeUnionToken = 0;
  let pendingShapeSiblings = null;
  let shapeSiblingMergeState = 'idle'; /* idle | running | done */

  function getCards() {
    return Array.from(document.querySelectorAll(CARD_SELECTOR));
  }

  function parseTags(str) {
    if (!str) return [];
    return str.split('|').map(function (t) { return t.trim(); }).filter(Boolean);
  }

  function cardTags(card) {
    return parseTags(card.dataset.ciwProductTags || '');
  }

  function cardMatchesTags(card, requiredTags) {
    if (!requiredTags.length) return true;
    const tags = cardTags(card);
    return requiredTags.some(function (tag) {
      return tags.some(function (t) { return t.toLowerCase() === tag.toLowerCase(); });
    });
  }

  function cardOnSale(card) {
    return card.dataset.ciwOnSale === 'true';
  }

  function cardBestSeller(card) {
    return card.dataset.ciwBestSeller === 'true';
  }

  function cardMeasurement(card, key) {
    const val = parseFloat(card.dataset[key === 'lens-width' ? 'ciwLensWidth' : 'ciwFrameWidth'] || '');
    return Number.isFinite(val) ? val : null;
  }

  function getOptionNatives(optionInput) {
    const optionEl = optionInput.closest('.ciw-gusa-filter-option');
    if (!optionEl) return [];
    const slug = optionInput.dataset.ciwGusaOptionSlug;
    return Array.from(optionEl.querySelectorAll(NATIVE_INPUT)).filter(function (native) {
      return native.dataset.ciwGusaOptionSlug === slug;
    });
  }

  function getOptionFamilyValues(optionInput) {
    const fromAttr = parseTags(optionInput.dataset.ciwGusaRawValues || '');
    if (fromAttr.length) return fromAttr;
    return getOptionNatives(optionInput)
      .map(function (native) {
        return native.value;
      })
      .filter(Boolean);
  }

  function pickPrimaryNative(natives) {
    if (!natives.length) return null;
    let primary = natives[0];
    natives.forEach(function (native) {
      const count = parseInt(native.dataset.ciwGusaValueCount || '0', 10);
      const primaryCount = parseInt(primary.dataset.ciwGusaValueCount || '0', 10);
      if (count > primaryCount) primary = native;
    });
    return primary;
  }

  function pickPrimaryFamilyValue(optionInput, family) {
    if (!family.length) return '';
    const natives = getOptionNatives(optionInput);
    const primaryNative = pickPrimaryNative(natives);
    if (primaryNative && primaryNative.value) return primaryNative.value;
    try {
      const active = new URLSearchParams(window.location.search).getAll(SHAPE_PARAM);
      for (let i = 0; i < family.length; i += 1) {
        if (active.indexOf(family[i]) !== -1) return family[i];
      }
    } catch (err) {
      /* ignore */
    }
    return family[0];
  }

  function readExpectedShapeCount(optionInput, natives, family) {
    const fromNatives = natives.reduce(function (sum, native) {
      return sum + (parseInt(native.dataset.ciwGusaValueCount || '0', 10) || 0);
    }, 0);
    if (fromNatives > 0 && natives.length >= family.length) return fromNatives;
    const labelText =
      (optionInput.closest('.ciw-gusa-filter-option') || optionInput.closest('label') || optionInput)
        .textContent || '';
    const match = labelText.match(/\((\d[\d,]*)\)/);
    if (match) {
      const n = parseInt(match[1].replace(/,/g, ''), 10);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return fromNatives || family.length;
  }

  function syncGroupOption(optionInput) {
    const optionEl = optionInput.closest('.ciw-gusa-filter-option');
    if (!optionEl) return;
    const slug = optionInput.dataset.ciwGusaOptionSlug;
    const group = optionInput.dataset.ciwGusaGroup;
    const checked = optionInput.checked;
    const natives = getOptionNatives(optionInput);

    if (natives.length <= 1) {
      natives.forEach(function (native) {
        native.checked = checked;
      });
    } else {
      /* Shopify keeps only the last repeated filter param — submit one value (highest count). */
      const primary = pickPrimaryNative(natives);
      natives.forEach(function (native) {
        native.checked = checked && native === primary;
      });
    }

    if (optionInput.hasAttribute('data-ciw-gusa-tag-only')) {
      const key = group + ':' + slug;
      if (checked) tagOnlyActive.set(key, parseTags(optionInput.dataset.ciwGusaTags || ''));
      else tagOnlyActive.delete(key);
    }

    const label = optionInput.closest('.facets__label');
    if (label) label.classList.toggle('active', checked);
  }

  function getPreviewThemeId() {
    try {
      return new URLSearchParams(window.location.search).get('preview_theme_id') || '';
    } catch (err) {
      return '';
    }
  }

  function clearShapeFilterParams(params) {
    const keys = [];
    params.forEach(function (_value, key) {
      if (key === SHAPE_GROUP_PARAM || key.toLowerCase().indexOf('shape') !== -1) {
        if (keys.indexOf(key) === -1) keys.push(key);
      }
    });
    keys.forEach(function (key) {
      params.delete(key);
    });
  }

  function buildShapeValueFetchUrl(shapeValue, page) {
    const params = new URLSearchParams(window.location.search);
    clearShapeFilterParams(params);
    params.delete('page');
    params.delete('section_id');
    params.set(SHAPE_PARAM, shapeValue);
    params.set('filter.v.availability', '1');
    if (page && page > 1) params.set('page', String(page));
    const grid = document.getElementById('product-grid');
    if (grid && grid.dataset.id) params.set('section_id', grid.dataset.id);
    const previewId = getPreviewThemeId();
    if (previewId) params.set('preview_theme_id', previewId);
    return window.location.pathname + '?' + params.toString();
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, ms);
    });
  }

  function isChallengeHtml(html) {
    return /verifying your connection|just a moment|cf-browser-verification|challenge-platform/i.test(html || '');
  }

  let shapeUnionExpectedCount = 0;

  function updateShapeUnionCounts(expectedCount) {
    if (Number.isFinite(expectedCount) && expectedCount > 0) {
      shapeUnionExpectedCount = expectedCount;
    }
    const grid = document.getElementById('product-grid');
    if (!grid) return;
    const loaded = grid.querySelectorAll(CARD_SELECTOR).length;
    /* Toolbar count = cards on screen; View More total may use the family expected count. */
    const text = loaded + (loaded === 1 ? ' product' : ' products');
    ['CiwProductCount', 'CiwProductCountMobile'].forEach(function (id) {
      const el = document.getElementById(id);
      if (el) el.textContent = text;
    });
    const loadedEl = document.querySelector('[data-view-more-loaded]');
    if (loadedEl) loadedEl.textContent = String(loaded);
    const viewMoreWrap = document.querySelector('.ciw-view-more-wrap');
    if (viewMoreWrap) {
      const total =
        shapeUnionExpectedCount > loaded ? shapeUnionExpectedCount : loaded;
      viewMoreWrap.dataset.total = String(total);
    }
  }

  function appendShapeCardsFromHtml(html, seen) {
    if (!html || isChallengeHtml(html)) return { added: 0, cardCount: 0, challenged: true };
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fetchedGrid = doc.getElementById('product-grid');
    const liveGrid = document.getElementById('product-grid');
    if (!fetchedGrid || !liveGrid) return { added: 0, cardCount: 0, challenged: false };
    const cards = Array.from(fetchedGrid.querySelectorAll(CARD_SELECTOR));
    let added = 0;
    const fragment = document.createDocumentFragment();
    cards.forEach(function (card) {
      const handle = card.dataset.handle;
      if (!handle || seen.has(handle)) return;
      seen.add(handle);
      const imported = document.importNode(card, true);
      imported.dataset.ciwSettled = 'true';
      fragment.appendChild(imported);
      added += 1;
    });
    if (added > 0) liveGrid.appendChild(fragment);
    return { added: added, cardCount: cards.length, challenged: false };
  }

  function fetchShapeValueWithRetry(shapeValue, page, attempt) {
    attempt = attempt || 0;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller
      ? window.setTimeout(function () {
          controller.abort();
        }, 8000)
      : null;
    return fetch(buildShapeValueFetchUrl(shapeValue, page), {
      credentials: 'same-origin',
      signal: controller ? controller.signal : undefined,
    })
      .then(function (response) {
        if ((response.status === 429 || response.status >= 500) && attempt < 2) {
          return delay(400 * (attempt + 1)).then(function () {
            return fetchShapeValueWithRetry(shapeValue, page, attempt + 1);
          });
        }
        if (!response.ok) return '';
        return response.text().then(function (html) {
          if (isChallengeHtml(html) && attempt < 2) {
            return delay(500 * (attempt + 1)).then(function () {
              return fetchShapeValueWithRetry(shapeValue, page, attempt + 1);
            });
          }
          return isChallengeHtml(html) ? '' : html;
        });
      })
      .catch(function () {
        if (attempt < 2) {
          return delay(400 * (attempt + 1)).then(function () {
            return fetchShapeValueWithRetry(shapeValue, page, attempt + 1);
          });
        }
        return '';
      })
      .finally(function () {
        if (timer) window.clearTimeout(timer);
      });
  }

  function mergeShapeSiblingValues(siblingValues, token, expectedCount) {
    const grid = document.getElementById('product-grid');
    if (!grid || !siblingValues.length) {
      updateShapeUnionCounts(expectedCount);
      return Promise.resolve();
    }

    updateShapeUnionCounts(expectedCount);

    const seen = new Set(
      Array.from(grid.querySelectorAll(CARD_SELECTOR))
        .map(function (card) {
          return card.dataset.handle;
        })
        .filter(Boolean)
    );

    return siblingValues
      .reduce(function (chain, shapeValue) {
        return chain.then(function () {
          if (token !== shapeUnionToken) return;
          return delay(280).then(function () {
            function loadPage(page) {
              if (token !== shapeUnionToken) return Promise.resolve();
              return fetchShapeValueWithRetry(shapeValue, page).then(function (html) {
                if (token !== shapeUnionToken) return;
                const result = appendShapeCardsFromHtml(html, seen);
                if (result.challenged || !html) return;
                updateShapeUnionCounts(expectedCount);
                /* Keep paging while this value still returns products (page size can be < 24). */
                if (result.cardCount > 0 && result.added > 0 && page < 8) {
                  return loadPage(page + 1);
                }
              });
            }
            return loadPage(1);
          });
        });
      }, Promise.resolve())
      .then(function () {
        if (token !== shapeUnionToken) return;
        updateShapeUnionCounts(expectedCount);
        document.dispatchEvent(
          new CustomEvent('ciw:gusa-shape-siblings-merged', { detail: { token: token } })
        );
      });
  }

  function queueShapeSiblingMerge(optionInput) {
    const family = getOptionFamilyValues(optionInput);
    if (family.length <= 1) {
      pendingShapeSiblings = null;
      return;
    }
    const natives = getOptionNatives(optionInput);
    const primaryValue = pickPrimaryFamilyValue(optionInput, family);
    const siblings = family.filter(function (value) {
      return value !== primaryValue;
    });
    if (!siblings.length) {
      pendingShapeSiblings = null;
      return;
    }
    const token = (shapeUnionToken += 1);
    pendingShapeSiblings = {
      token: token,
      slug: optionInput.dataset.ciwGusaOptionSlug,
      values: siblings,
      expectedCount: readExpectedShapeCount(optionInput, natives, family),
    };
  }

  function flushPendingShapeSiblingMerge() {
    if (!pendingShapeSiblings || !pendingShapeSiblings.values || !pendingShapeSiblings.values.length) {
      return Promise.resolve();
    }
    const pending = pendingShapeSiblings;
    pendingShapeSiblings = null;
    return mergeShapeSiblingValues(pending.values, pending.token, pending.expectedCount).then(
      function () {
        if (pending.slug) {
          document.documentElement.setAttribute('data-ciw-shape-siblings-merged', pending.slug);
        }
        shapeSiblingMergeState = 'done';
      },
      function () {
        shapeSiblingMergeState = 'idle';
      }
    );
  }

  /* Full-page shape loads check only the primary native — merge the sibling metafield values after paint. */
  function queueActiveShapeSiblingMergesFromDom() {
    const seen = new Set();
    document
      .querySelectorAll(OPTION_INPUT + '[data-ciw-gusa-group="shape"]:checked')
      .forEach(function (optionInput) {
        const slug = optionInput.dataset.ciwGusaOptionSlug;
        if (!slug || seen.has(slug)) return;
        seen.add(slug);
        queueShapeSiblingMerge(optionInput);
      });
    return flushPendingShapeSiblingMerge();
  }

  function scheduleActiveShapeSiblingMerges() {
    if (shapeSiblingMergeState !== 'idle') return;
    let attempts = 0;
    function tryRun() {
      if (shapeSiblingMergeState !== 'idle') return;
      attempts += 1;
      const checked = document.querySelector(
        OPTION_INPUT + '[data-ciw-gusa-group="shape"]:checked'
      );
      const grid = document.getElementById('product-grid');
      const cardCount = grid ? grid.querySelectorAll(CARD_SELECTOR).length : 0;
      const slug = checked && checked.dataset.ciwGusaOptionSlug;
      const family = checked ? getOptionFamilyValues(checked) : [];
      if ((!checked || cardCount < 1 || family.length <= 1) && attempts < 25) {
        window.setTimeout(tryRun, 200);
        return;
      }
      if (!checked || family.length <= 1) return;
      if (document.documentElement.getAttribute('data-ciw-shape-siblings-merged') === slug) {
        shapeSiblingMergeState = 'done';
        return;
      }
      shapeSiblingMergeState = 'running';
      queueActiveShapeSiblingMergesFromDom();
    }
    window.setTimeout(tryRun, 200);
  }

  function syncAllGusaNativesInForm(form) {
    if (!form) return;
    form.querySelectorAll(OPTION_INPUT).forEach(function (optionInput) {
      if (optionInput.hasAttribute('data-ciw-gusa-client-filter')) return;
      syncGroupOption(optionInput);
    });
  }

  function dispatchNativeInput(optionInput) {
    const optionEl = optionInput.closest('.ciw-gusa-filter-option');
    if (!optionEl) return;

    const form = optionInput.closest('form');
    if (form) {
      const root = form.querySelector(ROOT_SELECTOR);
      if (root) dedupeNativeInputs(root);
    }
    syncAllGusaNativesInForm(form);

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm && mobileForm !== form) {
      mobileForm.querySelectorAll(OPTION_INPUT).forEach(function (mobileInput) {
        const group = mobileInput.dataset.ciwGusaGroup;
        const slug = mobileInput.dataset.ciwGusaOptionSlug;
        if (!group || !slug) return;
        const desktopInput = form.querySelector(
          '[data-ciw-gusa-option-input][data-ciw-gusa-group="' + group + '"][data-ciw-gusa-option-slug="' + slug + '"]'
        );
        if (desktopInput) {
          mobileInput.checked = desktopInput.checked;
          syncGroupOption(mobileInput);
        }
      });
    }

    const native = form.querySelector(NATIVE_INPUT + ':not([disabled])');
    if (native) {
      native.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }
    applyClientFilters();
  }

  function readMeasurementFilters(root) {
    const lensMin = root.querySelector('[data-ciw-gusa-measurement-min="lens-width"]');
    const lensMax = root.querySelector('[data-ciw-gusa-measurement-max="lens-width"]');
    const frameMin = root.querySelector('[data-ciw-gusa-measurement-min="frame-width"]');
    const frameMax = root.querySelector('[data-ciw-gusa-measurement-max="frame-width"]');
    measurementFilters = {
      lensWidth: {
        min: lensMin && lensMin.value !== '' ? parseFloat(lensMin.value) : null,
        max: lensMax && lensMax.value !== '' ? parseFloat(lensMax.value) : null,
      },
      frameWidth: {
        min: frameMin && frameMin.value !== '' ? parseFloat(frameMin.value) : null,
        max: frameMax && frameMax.value !== '' ? parseFloat(frameMax.value) : null,
      },
    };
  }

  function applyClientFilters() {
    const hasTagFilters = tagOnlyActive.size > 0;
    const hasClientBadges = clientFiltersActive.size > 0;
    const hasMeasurements =
      measurementFilters.lensWidth.min != null ||
      measurementFilters.lensWidth.max != null ||
      measurementFilters.frameWidth.min != null ||
      measurementFilters.frameWidth.max != null;

    if (!hasTagFilters && !hasClientBadges && !hasMeasurements) {
      getCards().forEach(function (card) {
        card.classList.remove(CARD_HIDDEN);
      });
      updateVisibleCount();
      return;
    }

    const tagGroups = {};
    tagOnlyActive.forEach(function (tags, key) {
      const group = key.split(':')[0];
      if (!tagGroups[group]) tagGroups[group] = [];
      tagGroups[group].push(tags);
    });

    getCards().forEach(function (card) {
      let visible = true;

      Object.keys(tagGroups).forEach(function (group) {
        const groupRules = tagGroups[group];
        const groupMatch = groupRules.some(function (tags) {
          return cardMatchesTags(card, tags);
        });
        if (!groupMatch) visible = false;
      });

      if (clientFiltersActive.has('sale') && !cardOnSale(card)) visible = false;
      if (clientFiltersActive.has('best-seller') && !cardBestSeller(card)) visible = false;

      if (hasMeasurements) {
        const lensW = cardMeasurement(card, 'lens-width');
        const frameW = cardMeasurement(card, 'frame-width');
        if (measurementFilters.lensWidth.min != null && (lensW == null || lensW < measurementFilters.lensWidth.min)) visible = false;
        if (measurementFilters.lensWidth.max != null && (lensW == null || lensW > measurementFilters.lensWidth.max)) visible = false;
        if (measurementFilters.frameWidth.min != null && (frameW == null || frameW < measurementFilters.frameWidth.min)) visible = false;
        if (measurementFilters.frameWidth.max != null && (frameW == null || frameW > measurementFilters.frameWidth.max)) visible = false;
      }

      card.classList.toggle(CARD_HIDDEN, !visible);
    });

    updateVisibleCount();
    document.dispatchEvent(new CustomEvent('ciw:gusa-client-filtered'));
  }

  function updateVisibleCount() {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const visible = getCards().filter(function (c) {
      if (c.classList.contains(CARD_HIDDEN)) return false;
      if (
        isMobile &&
        c.classList.contains('ciw-mobile-plp-hidden') &&
        !c.classList.contains('ciw-mobile-plp-shown')
      ) {
        return false;
      }
      return true;
    }).length;
    const loadedEl = document.querySelector('[data-view-more-loaded]');
    if (loadedEl) loadedEl.textContent = String(visible);
    ['CiwProductCount', 'CiwProductCountMobile'].forEach(function (id) {
      const el = document.getElementById(id);
      if (el) el.textContent = visible + ' products';
    });
  }

  function dedupeNativeInputs(root) {
    const seen = new Map();
    root.querySelectorAll(NATIVE_INPUT).forEach(function (native) {
      const key = native.name + '\0' + native.value;
      if (seen.has(key)) {
        native.remove();
        return;
      }
      seen.set(key, native);
    });
  }

  function syncAllFromNative(root) {
    root.querySelectorAll('.ciw-gusa-filter-option').forEach(function (optionEl) {
      const optionInput = optionEl.querySelector(OPTION_INPUT);
      if (!optionInput || optionInput.hasAttribute('data-ciw-gusa-client-filter')) return;
      const natives = getOptionNatives(optionInput);
      if (!natives.length && !optionInput.hasAttribute('data-ciw-gusa-tag-only')) return;
      const checkedNatives = natives.filter(function (n) {
        return n.checked;
      });
      const anyChecked = checkedNatives.length > 0;
      /* Multi-mapped options intentionally check only the primary native. */
      optionInput.checked = anyChecked;
      optionInput.indeterminate = false;
      optionEl.querySelector('.facets__label')?.classList.toggle('active', anyChecked);
    });
    updateGroupCounts(root);
  }

  function updateGroupCounts(root) {
    root.querySelectorAll('[data-ciw-gusa-filter-group]').forEach(function (groupEl) {
      const groupId = groupEl.dataset.ciwGusaFilterGroup;
      if (!groupId || groupId === 'eyewear-type' || groupId === 'price' || groupId === 'measurements') return;
      let active = 0;
      groupEl.querySelectorAll(OPTION_INPUT + ':checked').forEach(function () { active += 1; });
      const countEl = document.querySelector('[data-ciw-gusa-selected-count="' + groupId + '"]');
      if (countEl) {
        countEl.textContent = '(' + active + ')';
        countEl.classList.toggle('hidden', active === 0);
      }
      const clearBtn = document.querySelector('[data-ciw-gusa-clear="' + groupId + '"]');
      if (clearBtn) clearBtn.classList.toggle('hidden', active === 0);
    });
  }

  function onChange(event) {
    const optionInput = event.target.closest(OPTION_INPUT);
    if (!optionInput) return;

    if (optionInput.hasAttribute('data-ciw-gusa-client-filter')) {
      const key = optionInput.dataset.ciwGusaClientFilter;
      if (optionInput.checked) clientFiltersActive.add(key);
      else clientFiltersActive.delete(key);
      optionInput.closest('.facets__label')?.classList.toggle('active', optionInput.checked);
      applyClientFilters();
      return;
    }

    if (optionInput.dataset.ciwGusaGroup === 'shape') {
      shapeSiblingMergeState = 'idle';
      shapeUnionExpectedCount = 0;
      document.documentElement.removeAttribute('data-ciw-shape-siblings-merged');
      if (optionInput.checked) queueShapeSiblingMerge(optionInput);
      else {
        shapeUnionToken += 1;
        pendingShapeSiblings = null;
      }
    }

    syncGroupOption(optionInput);
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      dispatchNativeInput(optionInput);
    }, 50);
  }

  function onMeasurementInput(event) {
    if (!event.target.matches(MEASUREMENT_MIN + ', ' + MEASUREMENT_MAX)) return;
    const root = event.target.closest(ROOT_SELECTOR);
    if (!root) return;
    readMeasurementFilters(root);
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(applyClientFilters, 200);
  }

  function onPillClick(event) {
    const pill = event.target.closest('[data-ciw-gusa-pill]');
    if (!pill) return;
    event.preventDefault();

    const group = pill.dataset.ciwGusaPill;
    const slug = pill.dataset.ciwGusaPillSlug;

    document.querySelectorAll('[data-ciw-gusa-option-input][data-ciw-gusa-group="' + group + '"][data-ciw-gusa-option-slug="' + slug + '"]').forEach(function (optionInput) {
      if (!optionInput.checked) return;
      optionInput.checked = false;
      syncGroupOption(optionInput);
    });

    const native = document.querySelector('[data-ciw-gusa-native-input][data-ciw-gusa-group="' + group + '"][data-ciw-gusa-option-slug="' + slug + '"]');
    if (native) {
      native.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      applyClientFilters();
      pill.remove();
      updateVisibleCount();
    }
  }

  function onClearClick(event) {
    const btn = event.target.closest('[data-ciw-gusa-clear]');
    if (!btn) return;
    event.preventDefault();
    const groupId = btn.dataset.ciwGusaClear;
    document.querySelectorAll('[data-ciw-gusa-group="' + groupId + '"]').forEach(function (input) {
      if (input.checked) {
        input.checked = false;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function revealDesktopBrandOptions(root) {
    if (!root || window.matchMedia('(max-width: 749px)').matches) return;
    const brandGroup = root.querySelector('[data-ciw-gusa-filter-group="brand"]');
    if (!brandGroup) return;
    const options = brandGroup.querySelectorAll('.ciw-gusa-filter-options > .ciw-gusa-filter-option');
    options.forEach(function (optionEl, index) {
      if (index >= 20) return;
      optionEl.classList.remove('hidden', 'show-more-item');
    });
  }

  function bindRoot(root) {
    if (!root || root.dataset.ciwGusaBound === 'true') return;
    root.dataset.ciwGusaBound = 'true';
    dedupeNativeInputs(root);
    syncAllFromNative(root);
    readMeasurementFilters(root);
    applyClientFilters();
    revealDesktopBrandOptions(root);
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(bindRoot);
    document.addEventListener('change', onChange);
    document.addEventListener('input', onMeasurementInput);
    document.addEventListener('click', onClearClick);
    document.addEventListener('click', onPillClick);
    document.addEventListener('ciw:collection-updated', function () {
      tagOnlyActive.clear();
      document.querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
        delete root.dataset.ciwGusaBound;
        bindRoot(root);
      });

      if (pendingShapeSiblings && pendingShapeSiblings.values && pendingShapeSiblings.values.length) {
        flushPendingShapeSiblingMerge();
      }
    });
    /* Full page shape URLs need sibling merge after grid + checked option exist. */
    scheduleActiveShapeSiblingMerges();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
